/**
 * Katalóg splitrov — správa typov splitrov (pridať/upraviť/zmazať).
 * Symetrické (jedna strata) aj asymetrické (pomer % / strata dB na každý výstup).
 */
import React, { useEffect, useState } from 'react';
import { useSplitterCatalogStore } from '../../stores/splitterCatalogStore';
import { SplitterCatalogItem, percentToLossDb, lossDbToPercent } from '../../utils/splitterCatalog';
import './Dialog.css';

interface Props {
  isOpen: boolean;
  onClose: () => void;
}

interface OutputRow {
  percent: number;
  loss_db: number;
}

const DEFAULT_EXCESS = 0.3;

const makeEqualOutputs = (count: number, excess: number): OutputRow[] => {
  const pct = 100 / Math.max(1, count);
  return Array.from({ length: count }, () => ({
    percent: round2(pct),
    loss_db: round2(percentToLossDb(pct, excess)),
  }));
};

const round2 = (n: number) => Math.round(n * 100) / 100;

const EMPTY = {
  name: '',
  is_symmetric: true,
  outputs_count: 4,
  total_loss_db: 7.0,     // pre symetrické
  excess_loss: DEFAULT_EXCESS, // pre asymetrické
  color: '',
  is_active: true,
  sort_order: 0,
};

const SplitterCatalogDialog: React.FC<Props> = ({ isOpen, onClose }) => {
  const { types, fetchTypes, createType, updateType, deleteType } = useSplitterCatalogStore();
  const [form, setForm] = useState<any>(EMPTY);
  const [outputs, setOutputs] = useState<OutputRow[]>(makeEqualOutputs(4, DEFAULT_EXCESS));
  const [editId, setEditId] = useState<number | null>(null);
  const [error, setError] = useState<string | null>(null);
  const [saving, setSaving] = useState(false);

  useEffect(() => {
    if (isOpen) fetchTypes(true);
  }, [isOpen, fetchTypes]);

  if (!isOpen) return null;

  const startEdit = (t: SplitterCatalogItem) => {
    setEditId(t.id ?? null);
    const asym = t.is_symmetric === false;
    const excess = asym && typeof t.total_loss_db === 'number' ? t.total_loss_db : DEFAULT_EXCESS;
    setForm({
      name: t.name,
      is_symmetric: !asym,
      outputs_count: t.outputs_count,
      total_loss_db: t.total_loss_db ?? 0,
      excess_loss: excess,
      color: t.color ?? '',
      is_active: t.is_active !== false,
      sort_order: t.sort_order ?? 0,
    });
    if (asym && Array.isArray(t.output_losses) && t.output_losses.length) {
      setOutputs(t.output_losses.map((db) => ({
        loss_db: round2(db as number),
        percent: round2(lossDbToPercent(db as number, excess)),
      })));
    } else {
      setOutputs(makeEqualOutputs(t.outputs_count, excess));
    }
    setError(null);
  };

  const resetForm = () => {
    setEditId(null);
    setForm(EMPTY);
    setOutputs(makeEqualOutputs(4, DEFAULT_EXCESS));
    setError(null);
  };

  // zmena počtu výstupov (asymetrické) — zväčší/zmenší pole
  const changeOutputsCount = (count: number) => {
    setForm({ ...form, outputs_count: count });
    if (!form.is_symmetric) {
      setOutputs((prev) => {
        const next = [...prev];
        while (next.length < count) next.push({ percent: 0, loss_db: round2(percentToLossDb(0.0001, form.excess_loss)) });
        next.length = count;
        return next;
      });
    }
  };

  // Po zmene výstupu i automaticky rozdelí zvyšok (do 100 %) medzi NASLEDUJÚCE výstupy
  const redistributeFrom = (rows: OutputRow[], i: number, excess: number): OutputRow[] => {
    const next = rows.map((r) => ({ ...r }));
    const after = next.length - i - 1;
    if (after > 0) {
      let sumUpTo = 0;
      for (let k = 0; k <= i; k++) sumUpTo += next[k].percent || 0;
      const per = round2(Math.max(0, 100 - sumUpTo) / after);
      for (let k = i + 1; k < next.length; k++) {
        next[k] = { percent: per, loss_db: round2(percentToLossDb(per, excess)) };
      }
    }
    return next;
  };

  const setPercent = (i: number, percent: number) => {
    setOutputs((prev) => {
      const next = prev.map((o, idx) =>
        idx === i ? { percent, loss_db: round2(percentToLossDb(percent, form.excess_loss)) } : { ...o });
      return redistributeFrom(next, i, form.excess_loss);
    });
  };
  const setLossDb = (i: number, loss_db: number) => {
    setOutputs((prev) => {
      const pct = round2(lossDbToPercent(loss_db, form.excess_loss));
      const next = prev.map((o, idx) =>
        idx === i ? { percent: pct, loss_db } : { ...o });
      return redistributeFrom(next, i, form.excess_loss);
    });
  };
  const changeExcess = (excess: number) => {
    setForm({ ...form, excess_loss: excess });
    // prepočítaj dB z % pri novej prídavnej strate
    setOutputs((prev) => prev.map((o) => ({ percent: o.percent, loss_db: round2(percentToLossDb(o.percent, excess)) })));
  };

  const percentSum = round2(outputs.reduce((a, o) => a + (o.percent || 0), 0));

  const handleSave = async () => {
    if (!form.name || !form.name.trim()) { setError('Zadaj názov typu (napr. 1:4).'); return; }
    if (!form.outputs_count || form.outputs_count < 1) { setError('Počet výstupov musí byť aspoň 1.'); return; }
    if (!form.is_symmetric) {
      if (Math.abs(percentSum - 100) > 1) {
        setError(`Súčet percent výstupov je ${percentSum} % — má byť ~100 %.`);
        return;
      }
    }
    setSaving(true);
    setError(null);
    try {
      const payload: any = {
        name: form.name.trim(),
        is_symmetric: !!form.is_symmetric,
        outputs_count: Number(form.outputs_count),
        color: form.color || null,
        is_active: !!form.is_active,
        sort_order: Number(form.sort_order) || 0,
      };
      if (form.is_symmetric) {
        payload.total_loss_db = form.total_loss_db === '' ? null : Number(form.total_loss_db);
        payload.output_losses = null;
      } else {
        payload.total_loss_db = Number(form.excess_loss) || 0; // excess uložený sem
        payload.output_losses = outputs.map((o) => Number(o.loss_db));
      }
      if (editId) await updateType(editId, payload);
      else await createType(payload);
      resetForm();
    } catch (e: any) {
      setError(e.message || 'Nepodarilo sa uložiť.');
    } finally {
      setSaving(false);
    }
  };

  const handleDelete = async (t: SplitterCatalogItem) => {
    if (!t.id) return;
    if (!window.confirm(`Naozaj zmazať typ splitra "${t.name}"?\n\nExistujúce dáta v kazetách zostanú nedotknuté.`)) return;
    try {
      await deleteType(t.id);
      if (editId === t.id) resetForm();
    } catch (e: any) {
      setError(e.message || 'Nepodarilo sa zmazať.');
    }
  };

  const inputStyle: React.CSSProperties = { padding: '6px 8px', border: '1px solid #cbd5e1', borderRadius: 6, width: '100%' };

  return (
    <div className="dialog-overlay" onClick={onClose}>
      <div className="dialog" style={{ maxWidth: 760 }} onClick={(e) => e.stopPropagation()}>
        <div className="dialog-header">
          <h2>🔀 Katalóg splitrov</h2>
          <button className="dialog-close" onClick={onClose}>✕</button>
        </div>

        <div className="dialog-body" style={{ color: '#1e293b', padding: 20, overflowY: 'auto' }}>
          {error && <div className="dialog-error">⚠️ {error}</div>}

          <div style={{ background: '#f8f9fa', padding: 16, borderRadius: 10, marginBottom: 16 }}>
            <div style={{ fontWeight: 600, marginBottom: 10 }}>{editId ? '✏️ Upraviť typ' : '➕ Pridať typ'}</div>

            <div style={{ display: 'grid', gridTemplateColumns: '1fr 1fr', gap: 12 }}>
              <div className="form-group">
                <label>Názov *</label>
                <input type="text" value={form.name}
                  onChange={(e) => setForm({ ...form, name: e.target.value })}
                  placeholder="napr. 1:4, Tap 90/10" />
              </div>
              <div className="form-group">
                <label>Počet výstupov *</label>
                <input type="number" min={1} value={form.outputs_count}
                  onChange={(e) => changeOutputsCount(Number(e.target.value))} />
              </div>
            </div>

            {/* Prepínač symetrický / asymetrický */}
            <div style={{ display: 'flex', gap: 8, margin: '12px 0' }}>
              <button type="button"
                onClick={() => setForm({ ...form, is_symmetric: true })}
                style={{ flex: 1, padding: '8px', borderRadius: 8, border: '1px solid #cbd5e1',
                  background: form.is_symmetric ? '#6366f1' : 'white', color: form.is_symmetric ? 'white' : '#1e293b', cursor: 'pointer' }}>
                Symetrický (rovnomerné delenie)
              </button>
              <button type="button"
                onClick={() => { setForm({ ...form, is_symmetric: false }); setOutputs(makeEqualOutputs(form.outputs_count, form.excess_loss)); }}
                style={{ flex: 1, padding: '8px', borderRadius: 8, border: '1px solid #cbd5e1',
                  background: !form.is_symmetric ? '#6366f1' : 'white', color: !form.is_symmetric ? 'white' : '#1e293b', cursor: 'pointer' }}>
                Asymetrický (pomer na výstup)
              </button>
            </div>

            {form.is_symmetric ? (
              <div className="form-group" style={{ maxWidth: 240 }}>
                <label>Vložná strata (dB)</label>
                <input type="number" step="0.1" value={form.total_loss_db}
                  onChange={(e) => setForm({ ...form, total_loss_db: e.target.value })} placeholder="napr. 7.0" />
              </div>
            ) : (
              <div style={{ marginTop: 4 }}>
                <div style={{ display: 'flex', gap: 12, alignItems: 'flex-end', marginBottom: 10 }}>
                  <div className="form-group" style={{ maxWidth: 180, margin: 0 }}>
                    <label>Prídavná strata (dB)</label>
                    <input type="number" step="0.1" value={form.excess_loss}
                      onChange={(e) => changeExcess(Number(e.target.value))} />
                  </div>
                  <div style={{ marginLeft: 'auto', fontWeight: 600, color: Math.abs(percentSum - 100) > 1 ? '#dc2626' : '#16a34a' }}>
                    Súčet: {percentSum} %
                  </div>
                </div>
                <table style={{ width: '100%', borderCollapse: 'collapse' }}>
                  <thead>
                    <tr style={{ textAlign: 'left', color: '#64748b', fontSize: 13 }}>
                      <th style={{ padding: 4 }}>Výstup</th>
                      <th style={{ padding: 4 }}>Podiel výkonu (%)</th>
                      <th style={{ padding: 4 }}>Strata (dB)</th>
                    </tr>
                  </thead>
                  <tbody>
                    {outputs.map((o, i) => (
                      <tr key={i}>
                        <td style={{ padding: 4 }}>#{i + 1}</td>
                        <td style={{ padding: 4 }}>
                          <input type="number" step="0.1" style={inputStyle} value={o.percent}
                            onChange={(e) => setPercent(i, Number(e.target.value))} />
                        </td>
                        <td style={{ padding: 4 }}>
                          <input type="number" step="0.1" style={inputStyle} value={o.loss_db}
                            onChange={(e) => setLossDb(i, Number(e.target.value))} />
                        </td>
                      </tr>
                    ))}
                  </tbody>
                </table>
                <div style={{ fontSize: 12, color: '#64748b', marginTop: 6 }}>
                  Zadaj % (alebo dB) na výstup — zvyšné výstupy sa automaticky dopočítajú do 100 %.
                  Príklad: zadáš #1 = 90 % a zvyšné porty sa samy rozdelia na zvyšných 10 %.
                </div>
              </div>
            )}

            <div style={{ display: 'grid', gridTemplateColumns: '1fr 1fr', gap: 12, marginTop: 12 }}>
              <div className="form-group">
                <label>Poradie</label>
                <input type="number" value={form.sort_order}
                  onChange={(e) => setForm({ ...form, sort_order: e.target.value })} />
              </div>
              <div className="form-group">
                <label>Farba (voliteľné)</label>
                <input type="color" value={form.color || '#3498db'}
                  onChange={(e) => setForm({ ...form, color: e.target.value })} style={{ height: 40, padding: 2, maxWidth: 80 }} />
              </div>
            </div>
            <div className="form-group" style={{ display: 'flex', alignItems: 'center', gap: 8 }}>
              <input type="checkbox" checked={form.is_active}
                onChange={(e) => setForm({ ...form, is_active: e.target.checked })} id="sp-active" />
              <label htmlFor="sp-active" style={{ margin: 0 }}>Aktívny (ponúkať vo výbere)</label>
            </div>

            <div style={{ display: 'flex', gap: 10, marginTop: 12 }}>
              <button className="btn-primary" onClick={handleSave} disabled={saving}>
                {saving ? 'Ukladám…' : editId ? '💾 Uložiť zmeny' : '➕ Pridať'}
              </button>
              {editId && <button className="btn-secondary" onClick={resetForm}>Zrušiť úpravu</button>}
            </div>
          </div>

          {/* Tabuľka typov */}
          <table style={{ width: '100%', borderCollapse: 'collapse' }}>
            <thead>
              <tr style={{ textAlign: 'left', borderBottom: '2px solid #e2e8f0' }}>
                <th style={{ padding: 8 }}>Názov</th>
                <th style={{ padding: 8 }}>Delenie</th>
                <th style={{ padding: 8 }}>Výstupy</th>
                <th style={{ padding: 8 }}>Strata</th>
                <th style={{ padding: 8 }}>Stav</th>
                <th style={{ padding: 8 }}>Akcie</th>
              </tr>
            </thead>
            <tbody>
              {types.map((t) => (
                <tr key={t.id ?? t.name} style={{ borderBottom: '1px solid #eef0f3', opacity: t.is_active === false ? 0.5 : 1 }}>
                  <td style={{ padding: 8, fontWeight: 500 }}>
                    {t.color && <span style={{ display: 'inline-block', width: 10, height: 10, borderRadius: '50%', background: t.color, marginRight: 6 }} />}
                    {t.name}
                  </td>
                  <td style={{ padding: 8 }}>{t.is_symmetric === false ? 'asymetrický' : 'symetrický'}</td>
                  <td style={{ padding: 8 }}>{t.outputs_count}</td>
                  <td style={{ padding: 8 }}>
                    {t.is_symmetric === false && Array.isArray(t.output_losses)
                      ? t.output_losses.map((d) => round2(d as number)).join(' / ') + ' dB'
                      : (t.total_loss_db != null ? `${t.total_loss_db} dB` : '—')}
                  </td>
                  <td style={{ padding: 8 }}>{t.is_active === false ? 'neaktívny' : 'aktívny'}</td>
                  <td style={{ padding: 8, display: 'flex', gap: 6 }}>
                    <button className="action-btn" title="Upraviť" onClick={() => startEdit(t)}>✏️</button>
                    <button className="action-btn" title="Zmazať" onClick={() => handleDelete(t)}>🗑️</button>
                  </td>
                </tr>
              ))}
            </tbody>
          </table>
        </div>
      </div>
    </div>
  );
};

export default SplitterCatalogDialog;
